// Stage 4A-2 + Stage AI-3:AI 記事生成 preview クライアント。
//
// 旧 POST /api/ai-blog/generate(Stage 4A, 同期一次性) と
// 新 POST /api/ai-blog/generate-stream(Stage AI-3, SSE streaming) を両方 export。
// デフォルトは streaming を使う(streamGenerate)。
// 型は backend/services/ai_blog_service.py の generate_article_preview の
// 戻り値構造に合わせる(英数字キー)。

import { apiFetch } from "./client";

// ---- Stage AI-3:SSE streaming コールバック型 ----

/** delta イベント受信時(逐チャンク append)。 */
export type OnStreamDelta = (text: string) => void;

/** done イベント受信時(final_text は空の可能性あり、streamedTextRef.current をフォールバック推奨)。 */
export type OnStreamDone = (status: string, finalText: string) => void;

/** error イベント受信時(code + message は backend が脱敏済み)。 */
export type OnStreamError = (code: string, message: string) => void;

// ---- Stage AI-3:cookie reader (csrf_token) ----
// apiFetch と同じく csrf_token cookie から取得。
// apiFetch は response.json() を待つので SSE では使えないため、最小限の cookie reader を inline。
function readCsrfCookie(): string {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : "";
}

// ---- 入力(payload)----

/**
 * POST /api/ai-blog/generate の payload。
 * keywords / purpose / length / target / cta / tone は UI でユーザーが選んだ値。
 * prompt_template_id は英数安定コード(UI では日文ラベル → 英語コードに翻訳して送る)。
 * prompt_text は textarea の現在値(空なら未送信)。
 */
export interface GenerateArticleRequest {
  keywords: string[];
  purpose?: "lead_generation" | "education" | "brand" | "promotion";
  length?: "short" | "medium" | "long";
  target?: string;
  cta?: string;
  tone?: "professional" | "friendly" | "story";
  prompt_template_id?:
    | "standard_blog"
    | "seo_focused"
    | "store_attraction"
    | "announcement"
    | "recruitment";
  prompt_text?: string;
}

export interface GenerateTitleRequest {
  article: string;
  template?: string;
  keyword?: string;
  purpose?: string;
  target?: string;
  instruction?: string;
}

export interface GenerateTitleResponse {
  titles: string[];
}

// ---- 出力(response)----

/** 見出し構成の 1 ノード。 */
export interface OutlineNode {
  h2: string;
  h3: string[];
}

/** 生成ログ 1 ステップ。 */
export interface GenerationStep {
  step: string;
  duration_ms: number;
}

/** API 使用量(mock モードでは全 0)。 */
export interface TokenUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

/** POST /api/ai-blog/generate 成功時の data フィールド。 */
export interface GenerateArticleResponse {
  generation_id: string;
  title_candidates: string[];
  selected_title: string;
  outline: OutlineNode[];
  body_preview: string;
  seo_score: number;
  generation_steps: GenerationStep[];
  token_usage: TokenUsage;
  prompt_template_used: string;
  source: "manual_ai";
}

// ---- Stage 4B:save-wordpress-draft ----

/**
 * POST /api/ai-blog/save-wordpress-draft の payload。
 * title / body は必須、generation_id / prompt_template_id は任意(監査用の紐付け)。
 */
export interface SaveDraftRequest {
  title: string;
  body: string;
  generation_id?: string;
  prompt_template_id?:
    | "standard_blog"
    | "seo_focused"
    | "store_attraction"
    | "announcement"
    | "recruitment";
}

/**
 * POST /api/ai-blog/save-wordpress-draft 成功時の data フィールド。
 * link は WP REST API が返す完全 URL(例: https://site.example/?p=123)。
 * save-wordpress-draft の直後は status="draft"。
 * AI Blog 画面内で公開 API と組み合わせるため、公開後 state では "publish" も扱う。
 * wordpress_post_id は WP 側で発行された post id。
 * title は保存時に送ったタイトル(再送受信として client 側で表示できる)。
 */
export interface SaveDraftResponse {
  status: "draft" | "publish" | string;
  wordpress_post_id: number;
  link: string;
  title: string;
  source: "manual_ai";
}

// ---- クライアント本体 ----

/**
 * AI 記事生成 preview を呼び出す(Stage 4A, 同期一次性,fallback 用途)。
 * 失敗時は apiFetch が ApiError(code / status / message / details)を throw。
 * Stage AI-3 以降は streamGenerate をデフォルトで使い、ここは残置(将来削除可)。
 */
export const aiBlogApi = {
  generate: (body: GenerateArticleRequest) =>
    apiFetch<GenerateArticleResponse>("/ai-blog/generate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * AI preview を WordPress draft として保存する(Stage 4B)。
   * backend は blog_service.create_draft を経由し、status は "draft" に固定される。
   * 戻り値の link は WP 側で発行された draft URL(未ログインで 404)。
   */
  saveDraft: (body: SaveDraftRequest) =>
    apiFetch<SaveDraftResponse>("/ai-blog/save-wordpress-draft", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  generateTitles: (body: GenerateTitleRequest) =>
    apiFetch<GenerateTitleResponse>("/ai-blog/title", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

// ---- Stage AI-3:streamGenerate(SSE) ----
//
// backend POST /api/ai-blog/generate-stream を fetch + ReadableStream で読み、
// SSE event(delta / done / error) を callback に分配する。
// 失敗時の扱い:
//   - HTTP 非 200(403 / 503 等)→ response.json() を読んで ApiError 化し、
//     onError(code, message) を呼んで return。refresh retry / fallback 切替なし。
//   - SSE error event → onError(code, message)(脱敏済みをそのまま渡す)
// AbortSignal 対応(将来 cancel UI で使えるよう受付だけ用意)。
//
// MVP では usage / job_started / keepalive は backend が転送しないので無視して良い。

export interface StreamCallbacks {
  onDelta: OnStreamDelta;
  onDone: OnStreamDone;
  onError: OnStreamError;
}

/**
 * POST /api/ai-blog/generate-stream を呼び、SSE を逐次消費する。
 * Promise は done / error / 非 200 / fetch 例外のいずれかで解決(以降何もしない)。
 */
export async function streamGenerate(
  body: GenerateArticleRequest,
  cb: StreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  const csrf = readCsrfCookie();
  let res: Response;
  try {
    res = await fetch("/api/ai-blog/generate-stream", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify(body),
      signal: signal ?? null,
    });
  } catch (e) {
    // fetch 自体が失敗(ネットワーク断 / abort 等)。abort は呼び出し元が知るべきだが
    // MVP では区別せず NETWORK_ERROR として通知。
    const msg = e instanceof Error ? e.message : "unknown fetch error";
    cb.onError("NETWORK_ERROR", msg);
    return;
  }

  if (!res.ok || !res.body) {
    // 非 200 → JSON エラー本文を読んで ApiError 風 code / message を抽出
    let code = `HTTP_${res.status}`;
    let message = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as {
        success?: boolean;
        error?: { code?: string; message?: string };
      };
      if (j && j.error) {
        code = j.error.code ?? code;
        message = j.error.message ?? message;
      }
    } catch {
      // JSON でない場合は status だけのフォールバック
    }
    cb.onError(code, message);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  let terminated = false;

  try {
    while (!terminated) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // SSE:event 行 + data 行 + 空行で 1 イベント
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const block = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const evMatch = block.match(/^event:\s*(.+)$/m);
        const dtMatch = block.match(/^data:\s*(.+)$/m);
        if (!evMatch || !dtMatch) continue;
        const kind = evMatch[1].trim();
        let payload: Record<string, unknown> = {};
        try {
          payload = JSON.parse(dtMatch[1]) as Record<string, unknown>;
        } catch {
          payload = {};
        }
        if (kind === "delta") {
          const text = typeof payload.text === "string" ? payload.text : "";
          cb.onDelta(text);
        } else if (kind === "done") {
          const status = typeof payload.status === "string" ? payload.status : "succeeded";
          const finalText = typeof payload.final_text === "string" ? payload.final_text : "";
          cb.onDone(status, finalText);
          terminated = true;
          break;
        } else if (kind === "error") {
          const code = typeof payload.code === "string" ? payload.code : "AUTH_AI_FAILED";
          const message = typeof payload.message === "string" ? payload.message : "AI エンジンでエラーが発生しました。";
          cb.onError(code, message);
          terminated = true;
          break;
        }
        // job_started / usage / keepalive は backend が転送しないので無視
      }
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : "stream read error";
    cb.onError("STREAM_READ_ERROR", msg);
  }
}
